/**
 * Whether a nameless public position is one of the organisation's own aircraft (MB's R238, 5 Nov 2026;
 * docs/specs/tracker.md §5.12). `onePerAircraft` joins positions by what they carry: a tracker asset, a registration, a
 * hex. That is the Diurnal tool's rule too (`crossSourceDedupKey`, am:1190; it had no position test). It cannot join a
 * pilot's tablet with no callsign set, which AvPlan sends with an empty callsign and Oz Runways as "Unknown" or
 * "NOSEND": MB's phone drew VH-JOZ on V2Track and, a little behind it on the same track, "AvPlan · AvPlan · Airborne".
 *
 * So such a position is compared with the organisation's own trackers, in time. The bar is set from the genuine
 * captures (`tests/fixtures/traffic`, table in the spec) and errs on the side of two icons:
 *
 * - Only a nameless position is ever joined this way. Anything that carries a name, a registration or a hex stays
 *   what it says it is, however close it flies: two aircraft with their own names on one track stay two (the capture
 *   of 27 Oct has VIPR beside NOSEND at 250 kt, and VH-YHU beside VH-ZES for five minutes).
 * - Only to the organisation's own trackers, never public to public: a wrong join there would hide an aircraft no one
 *   here can check, and two public icons cost little.
 * - Only while flying, so two aircraft on one strip stay separate however close they park.
 * - Several fixes over at least four minutes, each close in position, height and speed. One fix never decides.
 * - A nameless position that fits two of our aircraft fits neither (`onePerAircraft`).
 *
 * "Close in time" allows a slip of 45 seconds, because Oz Runways' times run about 38 seconds behind AvPlan's on the
 * same aircraft (measured in the same capture); that is what "a little behind it on the same track" is. A slip is
 * only forgiven along the track: the nameless fix has to lie on the tracker's own line.
 *
 * What the rule cannot do: GPS alone cannot tell a duplicate from a second aircraft holding station within about
 * 150 m of ours at the same height and speed for minutes, with a tablet that sends no name. Named aircraft and
 * aircraft further apart are never joined.
 */
import type { TrackedPosition } from './tracker.js';

/** The bar a nameless position has to clear. */
export const SAME_FLIGHT = {
  /** Only fixes of both sides moving at least this fast count: a strip or a parking bay proves nothing. */
  flyingKt: 45,
  /** How far off the tracker's line a fix may lie. */
  nearNm: 0.15,
  /** How far in time along that line (Oz Runways' times lag; AvPlan's and the trackers' do not). */
  slipMs: 45_000,
  /** The height difference: two tablets in one aircraft differ by up to 600 ft in the captures. */
  heightFt: 700,
  /** The speed difference. */
  speedKt: 30,
  /** At least this many fixes compared, this long from first to last. */
  minFixes: 3,
  minSpanMs: 240_000,
  /** The newest stretch compared, and the share of it that must be close (a sparse tracker cuts corners in a turn). */
  stretchMs: 600_000,
  closeShare: 0.75,
  /** Beyond this at any fix compared, or now, they are two aircraft, whatever the earlier fixes said. */
  apartNm: 1,
  /** Two tracker fixes further apart in time than this have no line between them. */
  gapMs: 240_000,
  /**
   * Nor do two fixes this far apart in heading: a tracker that reports each minute cuts the corner of a turn by more
   * than `nearNm`, so a fix in a turn is not compared at all, and a survey flight's line ends never split the icon.
   */
  turnDeg: 25,
} as const;

/** One fix of a track, oldest first. */
export type FlightFix = Pick<TrackedPosition, 'lat' | 'lon' | 'timeMs' | 'altFt' | 'speedKt' | 'headingDeg'>;

/** The names the feeds use for "no name": the tablet's owner set none. */
const NO_NAME = new Set(['', 'UNKNOWN', 'NOSEND', 'AVPLAN', 'OZ RUNWAYS']);

/**
 * An Oz Runways or AvPlan position with no name of its own: no registration, no hex, and a label that is empty, one of
 * the feeds' words for no name, without a letter ("000"), or (Oz Runways) only its own track key (`parseOzRunways`,
 * `parseAvPlan`).
 */
export function namelessPosition(
  p: Pick<TrackedPosition, 'source' | 'label' | 'deviceId' | 'registration' | 'icaoHex'>,
): boolean {
  if (p.source !== 'avplan' && p.source !== 'ozrunways') return false;
  if (p.registration || p.icaoHex) return false;
  const label = p.label.trim();
  return (
    NO_NAME.has(label.toUpperCase()) ||
    !/[A-Za-z]/.test(label) ||
    (p.source === 'ozrunways' && label === p.deviceId)
  );
}

const NM_PER_DEG = 60;

interface OnTrack {
  readonly nm: number;
  readonly altFt: number | null;
  readonly speedKt: number | null;
  /** That part of the track was a turn: its line is a chord, not where the aircraft flew. */
  readonly turning: boolean;
}

/** The two fixes of a segment disagree on the heading: the aircraft was turning between them. */
function turning(a: FlightFix, b: FlightFix): boolean {
  if (a.headingDeg === null || b.headingDeg === null) return false;
  const d = Math.abs(a.headingDeg - b.headingDeg) % 360;
  return Math.min(d, 360 - d) > SAME_FLIGHT.turnDeg;
}

/** The nearest the fix comes to the track's line within the slip: how far off it, and the height and speed there. */
function nearestOnTrack(fix: FlightFix, track: readonly FlightFix[]): OnTrack | null {
  const cos = Math.cos((fix.lat * Math.PI) / 180);
  const x = (p: FlightFix): number => (p.lon - fix.lon) * NM_PER_DEG * cos;
  const y = (p: FlightFix): number => (p.lat - fix.lat) * NM_PER_DEG;
  let best: OnTrack | null = null;
  for (let i = 1; i < track.length; i += 1) {
    const a = track[i - 1]!;
    const b = track[i]!;
    const span = b.timeMs - a.timeMs;
    if (span <= 0 || span > SAME_FLIGHT.gapMs) continue;
    if (b.timeMs < fix.timeMs - SAME_FLIGHT.slipMs || a.timeMs > fix.timeMs + SAME_FLIGHT.slipMs) continue;
    const dx = x(b) - x(a);
    const dy = y(b) - y(a);
    const len = dx * dx + dy * dy;
    // along the segment, kept to the part of it inside the slip
    const lo = Math.max(0, (fix.timeMs - SAME_FLIGHT.slipMs - a.timeMs) / span);
    const hi = Math.min(1, (fix.timeMs + SAME_FLIGHT.slipMs - a.timeMs) / span);
    const raw = len > 0 ? (-x(a) * dx - y(a) * dy) / len : 0;
    const t = Math.min(hi, Math.max(lo, raw));
    const nm = Math.hypot(x(a) + t * dx, y(a) + t * dy);
    if (best && nm >= best.nm) continue;
    best = {
      nm,
      altFt: a.altFt === null || b.altFt === null ? null : a.altFt + t * (b.altFt - a.altFt),
      speedKt: a.speedKt === null || b.speedKt === null ? null : a.speedKt + t * (b.speedKt - a.speedKt),
      turning: turning(a, b),
    };
  }
  return best;
}

const flying = (p: Pick<FlightFix, 'speedKt'>): boolean => (p.speedKt ?? 0) >= SAME_FLIGHT.flyingKt;

const apartNm = (a: FlightFix, b: FlightFix): number =>
  Math.hypot((a.lon - b.lon) * NM_PER_DEG * Math.cos((a.lat * Math.PI) / 180), (a.lat - b.lat) * NM_PER_DEG);

/**
 * Whether a nameless track flew the same flight as a tracker's: the newest ten minutes it has while flying, at least
 * three fixes over at least four minutes, three quarters of them on the tracker's line at its height and speed, none
 * a mile off.
 * Both tracks oldest first. Where the tablet is now has to agree too: flying, the tracker must still be reporting
 * alongside; on the ground (a tablet left on after landing), the two within a mile.
 */
export function sameFlight(nameless: readonly FlightFix[], tracker: readonly FlightFix[]): boolean {
  const newest = nameless[nameless.length - 1];
  const last = tracker[tracker.length - 1];
  if (!newest || !last) return false;
  if (flying(newest)) {
    const now = nearestOnTrack(newest, tracker);
    if (!now || now.nm > SAME_FLIGHT.apartNm) return false;
  } else if (apartNm(newest, last) > SAME_FLIGHT.apartNm) return false;

  const compared: { timeMs: number; close: boolean }[] = [];
  for (let i = nameless.length - 1; i >= 0; i -= 1) {
    const fix = nameless[i]!;
    if (!flying(fix)) continue;
    const near = nearestOnTrack(fix, tracker);
    if (!near || !flying(near)) continue;
    if (compared.length > 0 && compared[0]!.timeMs - fix.timeMs > SAME_FLIGHT.stretchMs) break;
    if (near.nm > SAME_FLIGHT.apartNm) return false;
    if (near.turning) continue;
    compared.push({
      timeMs: fix.timeMs,
      close:
        near.nm <= SAME_FLIGHT.nearNm &&
        fix.altFt !== null &&
        near.altFt !== null &&
        Math.abs(fix.altFt - near.altFt) <= SAME_FLIGHT.heightFt &&
        Math.abs((fix.speedKt ?? 0) - (near.speedKt ?? 0)) <= SAME_FLIGHT.speedKt,
    });
  }
  if (compared.length < SAME_FLIGHT.minFixes) return false;
  if (compared[0]!.timeMs - compared[compared.length - 1]!.timeMs < SAME_FLIGHT.minSpanMs) return false;
  return compared.filter((c) => c.close).length / compared.length >= SAME_FLIGHT.closeShare;
}
